import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import useSafeNavigate from "#web/hooks/useSafeNavigate";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { SaveBar, useAppBridge } from "@shopify/app-bridge-react";
import {
  Badge,
  Banner,
  Bleed,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Icon,
  InlineGrid,
  InlineStack,
  List,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { ClipboardIcon } from "@shopify/polaris-icons";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Error404 } from "../Error404";

const en = {
  activeFromToday: "Active from today",
  allCustomers: "All customers",
  back: "Protected Links",
  copyLink: "Copy Link",
  delete: "Delete",
  discountValue: "Discount Value",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "Error loading page",
  },
  linkActive: "Link is active.",
  percentageOffOrder: "Percentage off order",
  percentageOffOrderDescription: "The discount will be applied to the entire order.",
  performance: "Performance",
  save: "Save",
  secureLink: "Secure Link",
  subtitle: "Update the link to distribute codes. Codes can only be applied when using the provided link.",
  summary: "Summary",
  title: "Update discount link",
  type: "Type",
};

const DiscountLinkByIdQuery = ({ id }: { id: string | undefined }) =>
  queryOptions({
    enabled: !!id,
    queryFn: () => (id ? api.discountLinks.findById(id) : Promise.resolve(null)),
    queryKey: ["discountLinkById", id],
  });

export const LinkBlockingDetail = () => {
  const t = i18nLike(en);
  const navigate = useSafeNavigate();
  const { id } = useParams();
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const shopify = useAppBridge();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [originalDiscountValue, setOriginalDiscountValue] = useState<string>("");
  const [discountValue, setDiscountValue] = useState<string>("");
  const handleDiscountValueChang = useCallback((newValue: string) => {
    setDiscountValue(newValue);
  }, []);

  const [originalDiscountCode, setOriginalDiscountCode] = useState<string>("");
  const [discountCode, setDiscountCode] = useState<string>("");
  const handleDiscontCodeChange = useCallback((newValue: string) => {
    setDiscountCode(newValue);
  }, []);

  useEffect(() => {
    const hasChanged = discountCode !== originalDiscountCode || discountValue !== originalDiscountValue;
    if (hasChanged) {
      void shopify.saveBar.show("discount-link-save-bar");
    } else {
      void shopify.saveBar.hide("discount-link-save-bar");
    }
  }, [discountCode, discountValue]);

  const {
    data: discountLink,
    isError: discountLinkError,
    isFetching: discountLinkFetching,
  } = useQuery(DiscountLinkByIdQuery({ id }));

  useEffect(() => {
    if (discountLink) {
      setDiscountCode(discountLink.code ?? "");
      setDiscountValue(discountLink.value?.toString() ?? "");
      setOriginalDiscountCode(discountLink.code ?? "");
      setOriginalDiscountValue(discountLink.value?.toString() ?? "");
    }
  }, [discountLink]);

  const handleSave = async () => {
    setIsSaving(true);
    await api.discountLinks.update(id!, {
      code: discountCode,
      value: parseFloat(discountValue),
    });
    setOriginalDiscountCode(discountCode);
    setOriginalDiscountValue(discountValue);
    void shopify.saveBar.hide("discount-link-save-bar");
    setIsSaving(false);
  };

  const handleDiscard = () => {
    void shopify.saveBar.hide("discount-link-save-bar");
    setDiscountCode(originalDiscountCode);
    setDiscountValue(originalDiscountValue);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await api.discountLinks.delete(id!);
    void shopify.saveBar.hide("discount-link-save-bar");
    void shopify.toast.show("Link deleted");
    void navigate(routeConfig.linkblocking["/"]);
  };

  const handleCopyLink = () => {
    void navigator.clipboard.writeText(`${shop?.domain ?? ""}/?code=${discountCode}`);
    shopify.toast.show("Link copied to clipboard");
  };

  const PreviewCard = () => (
    <Card roundedAbove="sm">
      <BlockStack gap="400">
        <Text as="h6" fontWeight="semibold" variant="headingMd">
          {t("summary")}
        </Text>
        <Text as="h6" fontWeight="semibold" variant="headingMd">
          {`${shop?.domain ?? ""}/?code=${discountCode}`}
        </Text>
        <Button icon={ClipboardIcon} onClick={handleCopyLink}>
          {t("copyLink")}
        </Button>
        <Box>
          <Bleed marginInline={{ sm: "500", xs: "400" }}>
            <Divider />
          </Bleed>
        </Box>
        <BlockStack gap="150">
          <Text as="h6" variant="headingSm">
            {t("type")}
          </Text>
          <List type="bullet">
            <List.Item>{t("percentageOffOrder")}</List.Item>
            <List.Item>{t("secureLink")}</List.Item>
            <List.Item>{t("allCustomers")}</List.Item>
            <List.Item>{t("activeFromToday")}</List.Item>
          </List>
        </BlockStack>
        <BlockStack gap="150">
          <Text as="h6" variant="headingSm">
            {t("performance")}
          </Text>
          <Text as="p" tone="subdued">
            {t("linkActive")}
          </Text>
        </BlockStack>
      </BlockStack>
    </Card>
  );

  const pageLoading = discountLinkFetching || shopFetching;
  const isError = discountLinkError || shopError;

  if (!id) {
    return <Error404 />;
  }

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  return (
    <Page
      backAction={{
        content: t("back"),
        onAction: () => void navigate(routeConfig.linkblocking["/"]),
      }}
      subtitle={t("subtitle")}
      title={t("title")}
      titleMetadata={<Badge tone="info">Beta</Badge>}
    >
      <SaveBar id="discount-link-save-bar">
        <button
          disabled={isDeleting}
          loading={isSaving ? "" : undefined}
          onClick={() => void handleSave()}
          variant="primary"
        ></button>
        <button disabled={isDeleting || isSaving} onClick={handleDiscard}></button>
      </SaveBar>

      {pageLoading ? (
        <SkeletonBodyText />
      ) : (
        <InlineGrid columns={{ md: "2fr 1fr", xs: 1 }} gap="400">
          <BlockStack gap="400">
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text as="h6" fontWeight="semibold" variant="headingMd">
                    {t("percentageOffOrder")}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t("percentageOffOrderDescription")}
                  </Text>
                </BlockStack>
                <TextField
                  autoComplete="off"
                  label="Discount code"
                  onChange={handleDiscontCodeChange}
                  value={discountCode}
                />
                <TextField
                  autoComplete="off"
                  label="Link"
                  onFocus={handleCopyLink}
                  prefix={<Icon source={ClipboardIcon} />}
                  readOnly
                  value={`${shop?.domain ?? ""}/?code=${discountCode}`}
                />
              </BlockStack>
            </Card>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text as="h6" fontWeight="semibold" variant="headingMd">
                    {t("discountValue")}
                  </Text>
                </BlockStack>
                <TextField
                  autoComplete="off"
                  label="Percentage"
                  onChange={handleDiscountValueChang}
                  suffix="%"
                  type="number"
                  value={discountValue}
                />
              </BlockStack>
            </Card>
            <InlineStack align="end">
              <ButtonGroup>
                <Button
                  disabled={isSaving}
                  loading={isDeleting}
                  onClick={handleDelete}
                  tone="critical"
                  variant="primary"
                >
                  {t("delete")}
                </Button>
                <Button
                  disabled={!discountCode || !discountValue || isDeleting}
                  loading={isSaving}
                  onClick={handleSave}
                  variant="primary"
                >
                  {t("save")}
                </Button>
              </ButtonGroup>
            </InlineStack>
          </BlockStack>
          <BlockStack gap={{ md: "200", xs: "400" }}>
            <PreviewCard />
          </BlockStack>
        </InlineGrid>
      )}
    </Page>
  );
};
